// Ledger export: unified CSV + Excel + PDF for Daily Sales, Staff Meals,
// Comps and Spillage rows. Layout matches the on-screen table so the
// printed sheet reads the same as what the chef sees.
//
// Currency is resolved from the caller's payload OR (fallback) from
// prefs.getCurrency() so a chef in Australia sees "A$" and a chef in
// the UK sees "£". Nothing is ever hardcoded to USD.
import { buildCsv, downloadCsv, stampedFilename } from "./csvExport";
import { buildWorksheet, downloadWorkbook } from "./xlsxExport";
import { getCurrency } from "./prefs";

function resolveCurrency(payload) {
  return (payload && payload.currency) || getCurrency() || "USD";
}

function fmtMoney(n, currency) {
  const cur = currency || getCurrency() || "USD";
  try {
    return Number(n).toLocaleString(undefined, { style: "currency", currency: cur, maximumFractionDigits: 2 });
  } catch {
    return `${cur} ${Number(n).toFixed(2)}`;
  }
}

function fmtDateTime(iso) {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso || "");
    return d.toLocaleString();
  } catch {
    return String(iso || "");
  }
}

function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function normalize({ sales, deductions, recipeById, ingredientById, currency }) {
  const rows = [];
  for (const s of sales || []) {
    const r = recipeById[s.recipe_id];
    const qty = Number(s.quantity_sold ?? s.quantity ?? 0);
    const total = Number(s.revenue ?? 0);
    const unit_price = qty > 0 ? total / qty : Number(r?.sell_price ?? 0);
    rows.push({
      when: s.sold_at || s.created_at || s.date || "",
      kind: "Sale",
      item: s.recipe_name || r?.name || s.recipe_id || "",
      qty,
      unit_price,
      total,
      notes: "",
      currency,
    });
  }
  for (const d of deductions || []) {
    const r = recipeById[d.recipe_id];
    const ing = ingredientById[d.ingredient_id];
    // Backend uses `kind` = "staff_meal" | "comp" | "waste"; the on-screen
    // and printed label for waste is "Spillage" to match the tab in the UI.
    const rawKind = String(d.kind || "").toLowerCase();
    const kind = rawKind === "staff_meal" ? "Staff Meal"
      : rawKind === "comp" ? "Comp"
      : rawKind === "waste" || rawKind === "spillage" ? "Spillage"
      : d.kind || "Deduction";
    const name = d.recipe_name || r?.name || d.ingredient_name || ing?.name || d.notes || "";
    const notes = [d.notes, d.reason].filter(Boolean).join(" : ");
    // Show the food cost lost (or revenue lost for comps) as the row total
    // so the printed sheet doesn't have empty currency cells on deductions.
    const lostValue = Number(d.revenue_lost || 0) || Number(d.food_cost || 0) || 0;
    rows.push({
      when: d.created_at || d.date || "",
      kind,
      item: name,
      qty: Number(d.quantity || 0),
      unit_price: 0,
      total: lostValue,
      notes,
      currency,
    });
  }
  rows.sort((a, b) => String(b.when || "").localeCompare(String(a.when || "")));
  return rows;
}

export function exportLedgerCsv(payload) {
  const rows = normalize({ ...payload, currency: resolveCurrency(payload) });
  const columns = ledgerColumns();
  const csv = buildCsv(rows, columns);
  downloadCsv(stampedFilename("ledger", "csv"), csv);
}

export function exportLedgerXlsx(payload) {
  const currency = resolveCurrency(payload);
  const rows = normalize({ ...payload, currency });
  const columns = ledgerColumns();
  // Split into two sheets so finance can pivot on each independently.
  const sales = rows.filter((r) => r.kind === "Sale");
  const deductions = rows.filter((r) => r.kind !== "Sale");
  const sheets = [buildWorksheet(rows, columns, "Ledger", currency)];
  if (sales.length) sheets.push(buildWorksheet(sales, columns, "Sales", currency));
  if (deductions.length) sheets.push(buildWorksheet(deductions, columns, "Deductions", currency));
  downloadWorkbook(sheets, "ledger");
}

function ledgerColumns() {
  return [
    { label: "When", type: "date", get: (r) => r.when },
    { label: "Kind", type: "string", get: (r) => r.kind },
    { label: "Item", type: "string", get: (r) => r.item },
    { label: "Quantity", type: "number", get: (r) => r.qty },
    { label: "Unit price", type: "money", get: (r) => r.unit_price },
    { label: "Total", type: "money", get: (r) => r.total },
    { label: "Notes", type: "string", get: (r) => r.notes },
  ];
}

export function exportLedgerPdf(payload) {
  const currency = resolveCurrency(payload);
  const rows = normalize({ ...payload, currency });
  const salesTotal = rows.filter((r) => r.kind === "Sale").reduce((n, r) => n + r.total, 0);
  const deductions = rows.filter((r) => r.kind !== "Sale");
  const generated = new Date().toLocaleString();

  const rowHtml = rows.map((r) => `
    <tr>
      <td>${escapeHtml(fmtDateTime(r.when))}</td>
      <td>${escapeHtml(r.kind)}</td>
      <td class="name">${escapeHtml(r.item)}</td>
      <td class="right tabular">${r.qty.toFixed(2)}</td>
      <td class="right tabular">${r.unit_price ? fmtMoney(r.unit_price, currency) : ""}</td>
      <td class="right tabular strong">${r.total ? fmtMoney(r.total, currency) : ""}</td>
      <td class="notes">${escapeHtml(r.notes)}</td>
    </tr>
  `).join("");

  const html = `<!doctype html>
<html><head><meta charset="utf-8">
<title>Ledger export</title>
<style>
  :root { color-scheme: light; }
  @page { size: A4 portrait; margin: 18mm 14mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "SF Pro Text", "Inter", "Helvetica Neue", Arial, sans-serif; color: #0a0806; margin: 0; padding: 24px; background: #fff; }
  .header { display: flex; align-items: flex-end; justify-content: space-between; border-bottom: 2px solid #0a0806; padding-bottom: 12px; margin-bottom: 16px; }
  .brand { font-family: "GT Sectra", "Playfair Display", Georgia, serif; font-size: 28px; letter-spacing: 0.5px; }
  .brand .sub { display: block; font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase; color: #67563a; margin-top: 4px; }
  .meta { font-size: 10px; letter-spacing: 0.16em; text-transform: uppercase; color: #67563a; text-align: right; line-height: 1.55; }
  .hero { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin: 12px 0 20px; }
  .hero .card { border: 1px solid #d9cca6; border-radius: 10px; padding: 14px 16px; }
  .hero .eyebrow { font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase; color: #67563a; }
  .hero .value { font-family: "GT Sectra", "Playfair Display", Georgia, serif; font-size: 24px; margin-top: 4px; }
  table { width: 100%; border-collapse: collapse; font-size: 11px; }
  thead th { font-size: 9px; letter-spacing: 0.2em; text-transform: uppercase; color: #4a3e26; border-bottom: 1.5px solid #0a0806; padding: 8px 6px; text-align: left; }
  thead th.right { text-align: right; }
  tbody td { padding: 7px 6px; border-bottom: 1px solid #ece1c5; vertical-align: top; }
  td.right { text-align: right; }
  td.tabular { font-feature-settings: "tnum"; font-variant-numeric: tabular-nums; }
  td.strong { font-weight: 600; }
  td.name { font-weight: 500; }
  td.notes { color: #6b5a3d; font-style: italic; font-size: 10px; }
  tfoot td { padding: 10px 6px; border-top: 2px solid #0a0806; font-weight: 600; }
  .footnote { margin-top: 20px; font-size: 9px; color: #7a6a4a; text-align: center; letter-spacing: 0.12em; text-transform: uppercase; }
</style>
</head><body>
  <div class="header">
    <div class="brand">CulinaireSpecialist<span class="sub">Ledger Export</span></div>
    <div class="meta">
      Generated ${escapeHtml(generated)}<br>
      ${rows.length} row${rows.length === 1 ? "" : "s"}
    </div>
  </div>

  <div class="hero">
    <div class="card">
      <div class="eyebrow">Sales total</div>
      <div class="value">${escapeHtml(fmtMoney(salesTotal, currency))}</div>
    </div>
    <div class="card">
      <div class="eyebrow">Deductions logged</div>
      <div class="value">${deductions.length}</div>
    </div>
  </div>

  <table>
    <thead>
      <tr>
        <th>When</th>
        <th>Kind</th>
        <th>Item</th>
        <th class="right">Qty</th>
        <th class="right">Unit price</th>
        <th class="right">Total</th>
        <th>Notes</th>
      </tr>
    </thead>
    <tbody>${rowHtml || `<tr><td colspan="7" style="text-align:center; padding:24px; color:#7a6a4a;">No ledger activity in this range.</td></tr>`}</tbody>
    <tfoot>
      <tr>
        <td colspan="5" style="text-align:right;">Sales total</td>
        <td class="right tabular">${escapeHtml(fmtMoney(salesTotal, currency))}</td>
        <td></td>
      </tr>
    </tfoot>
  </table>

  <div class="footnote">Every calculation is deterministic, reproducible and audit-safe.</div>

<script>window.onload = () => setTimeout(() => window.print(), 350);</script>
</body></html>`;

  const win = window.open("", "_blank", "width=1100,height=1400");
  if (!win) return;
  win.document.open();
  win.document.write(html);
  win.document.close();
}
